import { PAPER_TYPES } from './constants';
import { PaperPreviewHandler } from './paper-preview-handler';
import { CombinedPaperTypeAndOrientation } from './types/combined-paper-type-and-orientation.type';
import { PaperDimensions } from './types/paper-dimensions.type';
import { calculateNumberOfRowsPerPage } from './utils/calculate-number-of-rows-per-page.fn';
import { nextTick } from './utils/next-tick.fn';
import { range } from './utils/range.fn';

export class LabelPreviewHandler extends PaperPreviewHandler {
  async renderPreview(
    paperType: CombinedPaperTypeAndOrientation,
    leftMargin: number,
    topMargin: number,
    rightMargin: number,
    bottomMargin: number,
    labelGutterHeight: number,
    labelGutterWidth: number,
    labelHeight: number,
    labelWidth: number,
    numberOfColumns: number,
  ) {
    const paperDimensions = PAPER_TYPES[paperType];

    await this.renderPageMargins(
      paperType,
      leftMargin,
      topMargin,
      rightMargin,
      bottomMargin,
    );

    await nextTick(() => {
      this.getChild('.labels')?.remove();

      const numberOfRows = calculateNumberOfRowsPerPage(
        paperDimensions,
        topMargin,
        bottomMargin,
        labelHeight,
        labelGutterHeight,
      );

      const labels = this.createGroup();
      labels.classList.add('labels');
      this.appendChild(labels);

      this.createLabels(
        labels,
        labelWidth,
        labelHeight,
        labelGutterWidth,
        labelGutterHeight,
        leftMargin,
        topMargin,
        numberOfRows,
        numberOfColumns,
      );
    });
  }

  private createLabels(
    labels: SVGElement,
    labelWidth: number,
    labelHeight: number,
    labelGutterWidth: number,
    labelGutterHeight: number,
    leftMargin: number,
    topMargin: number,
    numberOfRows: number,
    numberOfColumns: number,
  ) {
    for (const rowIndex of range(0, numberOfRows - 1)) {
      this.createRow(
        labels,
        rowIndex,
        labelWidth,
        labelHeight,
        labelGutterWidth,
        labelGutterHeight,
        leftMargin,
        topMargin,
        numberOfColumns,
      );
    }

    return labels;
  }

  private createRow(
    labels: SVGElement,
    rowIndex: number,
    labelWidth: number,
    labelHeight: number,
    labelGutterWidth: number,
    labelGutterHeight: number,
    leftMargin: number,
    topMargin: number,
    numberOfColumns: number,
  ) {
    const row = this.createGroup();

    labels.appendChild(row);

    for (const labelIndex of range(0, numberOfColumns - 1)) {
      this.createLabel(
        row,
        rowIndex,
        labelIndex,
        labelWidth,
        labelHeight,
        labelGutterWidth,
        labelGutterHeight,
        leftMargin,
        topMargin,
      );
    }

    return row;
  }

  private createLabel(
    row: SVGElement,
    rowIndex: number,
    labelIndex: number,
    labelWidth: number,
    labelHeight: number,
    labelGutterWidth: number,
    labelGutterHeight: number,
    leftMargin: number,
    topMargin: number,
  ) {
    const isFirstRow = rowIndex === 0;
    const isFirstLabel = labelIndex === 0;
    const labelX = isFirstLabel
      ? leftMargin + labelIndex * labelWidth
      : leftMargin + labelIndex * labelWidth + labelIndex * labelGutterWidth;
    const labelY = isFirstRow
      ? topMargin + rowIndex * labelHeight
      : topMargin + rowIndex * labelHeight + rowIndex * labelGutterHeight;
    const square = this.createLineSquare(
      labelX,
      labelY,
      labelWidth,
      labelHeight,
      'red',
    );

    row.appendChild(square);
  }
}
